import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import styled from "styled-components";
import type {
  DividerCdtLevel,
  DividerHardware,
  DividerPolicy,
  LoaderMode,
} from "@CommonTypes/backend/PspRules";
import { DIVIDER_CDT_LEVELS } from "@CommonTypes/backend/PspRules";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { LoaderLibraryShelf } from "@CommonUtils/pog/dividerPolicy";
import { Color, Flex, InlineMessage, NumberInput, Search, Select, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { DIVIDER_RULE_ERROR_KEYS, formatLength } from "src/utils/lengthUnit";
import { RulesSection } from "./components/RulesSection";
import { OSummary, type OSummaryItem } from "./components/OSummary";
import { TellO } from "./tellO/TellO";
import type { TellOInterpret, TellOSaveShelf } from "./tellO/tellOApi";
import {
  fixtureRowsAsSaved,
  libraryShelvesAsSaved,
  type TellOWindowData,
} from "./tellO/tellOModel";
import type { TellOShelfProposal } from "@CommonUtils/pog/tellO";
import {
  baseDividerDataLooksWrong,
  baseDividerHardware,
  baseShelfDividerResults,
  clearAllTicks,
  describeBaseShelfResult,
  describeBaseShelfResultWithLoader,
  describeBaseShelfTotal,
  describeDividersForO,
  dividerGroupLevelLabel,
  dividerHardwareInUse,
  dividerPolicyProblem,
  fromDisplayLength,
  groupProductsByCdt,
  productGroupValue,
  productsWithDividers,
  resetToBase,
  setProductsTicked,
  toDisplayLength,
  type DividerLevelNames,
  type DividerRulesProduct,
  type FixtureDividerRows,
  type TickColumn,
} from "./dividerRulesModel";
import {
  LOADER_MODES,
  describeLoadersForO,
  describeRowLoader,
  describeRowLoaderWithCount,
  loadersInPlay,
  loaderDecisionsByStoredShelf,
  loaderDepthInUse,
  loaderDepthError,
  loaderDepthNoteSource,
  loaderRulesState,
  withLoaderMode,
  withPrefilledLoaderDepth,
} from "./loaderRulesModel";

/** Above this many products the list gets a search box. */
const SEARCH_FROM = 12;

const NO_HARDWARE: DividerHardware = { width: 0, height: 0, depth: 0, atStart: true, atEnd: true };

const NO_LIBRARY_SHELVES: LoaderLibraryShelf[] = [];

/** The two tick columns, in order, with the key of their header (also the box label). */
const COLUMNS = [
  { column: "dividers", header: "planogramRules.dividers.columnDividers" },
  { column: "loaders", header: "planogramRules.dividers.columnLoaders" },
] as const satisfies readonly { column: TickColumn; header: string }[];

const MODE_KEYS = {
  products: "planogramRules.dividers.loaderModes.products",
  shelf: "planogramRules.dividers.loaderModes.shelf",
} as const satisfies Record<LoaderMode, string>;

const NOTE_KEYS = {
  psa: "planogramRules.dividers.fromBase",
  library: "planogramRules.dividers.fromFixtures",
  mixed: "planogramRules.dividers.fromBoth",
} as const;

/** Widths of the list's leading cells: the expander, then one cell per tick column. */
const EXPANDER_WIDTH = "22px";
const TICK_WIDTHS: Record<TickColumn, string> = { dividers: "56px", loaders: "84px" };

const CheckRow = styled.label<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${Color.textMain};
  cursor: ${({ isDisabled }) => (isDisabled ? "default" : "pointer")};
  min-width: 0;

  input {
    margin: 0;
    accent-color: ${Color.primary};
  }
`;

/** One list row: expander, one box per tick column, then the name. */
const ListRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: ${Color.textMain};
  min-width: 0;

  input {
    margin: 0;
    accent-color: ${Color.primary};
  }
`;

/** A fixed-width cell, so each column's boxes line up under its header. */
const Cell = styled.span<{ width: string }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: ${({ width }) => width};
`;

const ColumnHeader = styled(Cell)`
  font-size: 11px;
  color: ${Color.textSecondary};
  white-space: nowrap;
`;

/** The muted note after the column headers, next to "Spring loader". */
const ColumnNote = styled.span`
  font-size: 11px;
  color: ${Color.textSecondary};
  white-space: nowrap;
  padding-left: 4px;
`;

const RowName = styled.span`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  padding-left: 4px;
`;

const Muted = styled.span`
  color: ${Color.textSecondary};
  font-size: 12px;
`;

/** The muted part of a result row, in the row's own size. */
const RowDetail = styled.span`
  color: ${Color.textSecondary};
`;

const LinkButton = styled.button`
  font: inherit;
  font-size: 12px;
  padding: 0;
  border: 0;
  background: none;
  color: ${Color.primary};
  text-decoration: underline;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${Color.primaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${Color.textSecondary};
    cursor: default;
    text-decoration: none;
  }
`;

const ExpandButton = styled.button`
  font: inherit;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: none;
  color: ${Color.textSecondary};
  cursor: pointer;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Disclosure = styled.details`
  font-size: 13px;
  color: ${Color.textSecondary};

  summary {
    cursor: pointer;
    width: fit-content;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 10px;
  padding: 0 10px 10px;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 10px;
  background: ${Color.white};
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 3px;
`;

const Scroll = styled.div`
  max-height: 340px;
  overflow-y: auto;
`;

const SizeField = styled.label`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;
  gap: 8px;
  font-size: 13px;
  color: ${Color.textMain};
`;

/** The one choice of the section: two options side by side, one picked. */
const Segments = styled.div`
  display: inline-flex;
  max-width: 100%;
  border: 1px solid ${Color.primary};
  border-radius: 3px;
  overflow: hidden;
`;

const Segment = styled.button`
  font: inherit;
  font-size: 12px;
  padding: 4px 10px;
  border: 0;
  background: ${Color.white};
  color: ${Color.primary};
  cursor: pointer;
  white-space: nowrap;

  & + & {
    border-left: 1px solid ${Color.primary};
  }

  &[aria-checked="true"] {
    background: ${Color.primary};
    color: ${Color.white};
    cursor: default;
  }

  &:hover:not(:disabled):not([aria-checked="true"]) {
    background: ${Color.greenSmoke};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: -2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const FieldError = styled.span`
  color: ${Color.red};
  font-size: 12px;
`;

/** A native checkbox that can show "some ticked". */
const TriStateBox = ({
  state,
  onChange,
  label,
  isDisabled,
}: {
  state: boolean | "mixed";
  onChange: (isTicked: boolean) => void;
  label: string;
  isDisabled?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "mixed";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={state === true}
      disabled={isDisabled}
      onChange={() => onChange(state !== true)}
    />
  );
};

/** One size in the sizes row, with its note and its error under it. */
const Size = ({
  field,
  label,
  value,
  setValue,
  unit,
  note,
  error,
  isFocused,
  isDisabled,
}: {
  field: string;
  label: string;
  value: number;
  setValue: (value: number) => void;
  unit: string;
  note: ReactNode;
  error: string | null;
  isFocused: boolean;
  isDisabled?: boolean;
}) => (
  <Flex column gap="2px">
    <SizeField data-field={field} aria-disabled={isDisabled || undefined}>
      <span>{label}</span>
      <Flex width="90px">
        <NumberInput
          title=""
          value={value}
          setValue={setValue}
          min={0}
          isDisabled={isDisabled}
          isFocusedAutomatically={isFocused}
        />
      </Flex>
      {unit && <span>{unit}</span>}
    </SizeField>
    {note && <Muted data-note={field}>{note}</Muted>}
    {error && <FieldError role="alert">{error}</FieldError>}
  </Flex>
);

/**
 * "Spring loaders": Follow the products (the default, also when the rule has
 * no choice stored) or Stay with the shelf. No model call; picking an option
 * writes `loaderMode` on the rule.
 */
const LoaderModeChoice = ({
  mode,
  setMode,
  isDisabled,
}: {
  mode: LoaderMode;
  setMode: (mode: LoaderMode) => void;
  isDisabled?: boolean;
}) => {
  const { t } = useTranslation("common");
  const labelId = useId();
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // A radio group is one tab stop (the picked option); the arrow keys move and pick.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const count = LOADER_MODES.length;
    const next = (index + step + count) % count;
    setMode(LOADER_MODES[next]);
    optionRefs.current[next]?.focus();
  };
  return (
    <Flex column gap="2px">
      <SizeField as="div" data-field="loader-mode">
        <span id={labelId}>{t("planogramRules.dividers.loaderMode")}</span>
        <Segments role="radiogroup" aria-labelledby={labelId}>
          {LOADER_MODES.map((option, index) => (
            <Segment
              key={option}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={mode === option}
              tabIndex={mode === option ? 0 : -1}
              disabled={isDisabled}
              onClick={() => setMode(option)}
              onKeyDown={(event) => onKeyDown(event, index)}
            >
              {t(MODE_KEYS[option])}
            </Segment>
          ))}
        </Segments>
      </SizeField>
      {mode === "shelf" && (
        <Muted data-note="loader-mode">{t("planogramRules.dividers.shelfModeNote")}</Muted>
      )}
    </Flex>
  );
};

interface Props {
  policy: DividerPolicy;
  setPolicy: (policy: DividerPolicy) => void;
  basePog: PogNonCircular | null;
  products: DividerRulesProduct[];
  /** The merchant's length unit (stored lengths are x100 of it), passed by the rules modal. */
  unit: string;
  /** The category's CDT level names, where the CDT pages show them. */
  levelNames?: DividerLevelNames;
  /** The base store's Fixtures page rows (loader depths), from the base pog GET's `loaders`. */
  loaderLibraryShelves?: LoaderLibraryShelf[];
  /**
   * The base store's fixture rows with their dividers, from the base pog GET's
   * `fixtureDividerRows`: the keep switch's result rows and counts read them.
   * Null (no base store resolved): no shelf is claimed as kept. Absent: the
   * base planogram's own shelf dividers, as before.
   */
  fixtureDividerRows?: FixtureDividerRows;
  isLoading?: boolean;
  isDisabled?: boolean;
  /** Free-text interpretation is unavailable while editing the base planogram. */
  isFreeTextDisabled?: boolean;
  area?: string;
  /** More of O's sentences after the dividers and spring-loader ones. */
  moreSummaryItems?: OSummaryItem[];

  baseStoreCode?: string | null;

  basePogId?: number | null;
  interpret?: TellOInterpret;

  saveShelf?: TellOSaveShelf;
}

export const DividerRules = ({
  policy,
  setPolicy,
  basePog,
  products,
  unit,
  levelNames,
  loaderLibraryShelves: loadedLibraryShelves = NO_LIBRARY_SHELVES,
  fixtureDividerRows: loadedFixtureRows,
  isLoading = false,
  isDisabled,
  isFreeTextDisabled = false,
  area = "dividers",
  moreSummaryItems = [],
  baseStoreCode = null,
  basePogId = null,
  interpret,
  saveShelf: saveShelfAsked,
}: Props) => {
  const { t } = useTranslation("common");
  // Undo must read the last saved shelf value, including earlier Applies in this window.

  const [savedShelves, setSavedShelves] = useState<readonly TellOShelfProposal[]>([]);
  const fixtureDividerRows = useMemo(
    () => fixtureRowsAsSaved(loadedFixtureRows, savedShelves, basePog),
    [loadedFixtureRows, savedShelves, basePog],
  );
  const loaderLibraryShelves = useMemo(
    () => libraryShelvesAsSaved(loadedLibraryShelves, savedShelves),
    [loadedLibraryShelves, savedShelves],
  );
  const saveShelf: TellOSaveShelf | undefined = useMemo(
    () =>
      saveShelfAsked &&
      (async (proposal) => {
        const notSaved = await saveShelfAsked(proposal);
        if (notSaved === null) setSavedShelves((saved) => [...saved, proposal]);
        return notSaved;
      }),
    [saveShelfAsked],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [previous, setPrevious] = useState<DividerPolicy | null>(null);
  const headingId = useId();
  const [search, setSearch] = useState("");
  const [groupLevel, setGroupLevel] = useState<DividerCdtLevel>("cdt1");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const baseHardware = useMemo(() => baseDividerHardware(basePog), [basePog]);
  const hardware = dividerHardwareInUse(policy, baseHardware);
  const isWidthFromBase = !((policy.hardware?.width ?? 0) > 0) && baseHardware !== null;
  const dividerTicks = useMemo(
    () => productsWithDividers(policy, products, basePog),
    [policy, products, basePog],
  );
  const keep = policy.keepFixtureDividers === true;
  const shelves = useMemo(
    () => baseShelfDividerResults(basePog, dividerTicks, keep, fixtureDividerRows),
    [basePog, dividerTicks, keep, fixtureDividerRows],
  );
  const widthProblem =
    dividerPolicyProblem(policy, { needing: dividerTicks.size, hardware }) &&
    t("planogramRules.dividers.errors.widthMissing");
  // The fallback to no dividers ran once on load (useDividerFallbackOnLoad); here the
  // bad data only disables Reset and explains itself.
  const badData = useMemo(() => baseDividerDataLooksWrong(basePog), [basePog]);

  const loaders = useMemo(
    () => loaderRulesState(policy, products, basePog, loaderLibraryShelves),
    [policy, products, basePog, loaderLibraryShelves],
  );
  const loaderDecisions = useMemo(
    () => loaderDecisionsByStoredShelf(basePog, loaders.summary),
    [basePog, loaders.summary],
  );
  const depthError = loaderDepthError(policy, loaders);
  const depthProblem =
    depthError &&
    t(DIVIDER_RULE_ERROR_KEYS[depthError.code], {
      depth: depthError.params ? formatLength(depthError.params.depth, unit) : "",
    });
  const noteSource = loaderDepthNoteSource(policy, loaders.evidence);
  const isShelfMode = loaders.mode === "shelf";
  const ticked = { dividers: dividerTicks, loaders: loaders.ticked };

  const loaderSentence = isLoading
    ? null
    : describeLoadersForO({ policy, basePog, products, state: loaders, unit });
  const summary: OSummaryItem[] = [
    {
      key: "dividers",
      text: describeDividersForO({
        policy,
        basePog,
        products,
        unit,
        isLoading,
        fixtureRows: fixtureDividerRows,
      }),
    },
    ...(loaderSentence ? [{ key: "spring-loaders", text: loaderSentence }] : []),
    ...(isLoading ? [] : moreSummaryItems),
  ];

  const query = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const shown = query
      ? products.filter((product) =>
          [product.name, product.productCode, productGroupValue(product, groupLevel)].some((text) =>
            text?.toLowerCase().includes(query),
          ),
        )
      : products;
    return groupProductsByCdt(shown, groupLevel);
  }, [products, groupLevel, query]);

  // The first tick edit stores the depth the sizes row shows (prefilled from the
  // base), so the shelves the new ticks bring a loader to have a depth.
  const setTicked = (column: TickColumn, codes: string[], isTicked: boolean) =>
    setPolicy(
      setProductsTicked(
        withPrefilledLoaderDepth(policy, loaders.evidence),
        ticked,
        products,
        column,
        codes,
        isTicked,
      ),
    );

  const setHardware = (change: Partial<DividerHardware>) =>
    setPolicy({ ...policy, hardware: { ...(hardware ?? NO_HARDWARE), ...change } });

  const setLoaderDepth = (value: number) => {
    const { loaderDepth: _previous, ...rest } = policy;
    const depth = fromDisplayLength(value);
    // 0 or blank: each base shelf's own depth, as the backend reads 0.
    setPolicy(depth > 0 ? { ...rest, loaderDepth: depth } : rest);
  };

  const toggleExpanded = (value: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const nothingTicked = dividerTicks.size === 0 && loaders.ticked.size === 0;
  const withLoaders = loadersInPlay(loaders);

  const tellOData: TellOWindowData = useMemo(
    () => ({
      basePog,
      products,
      unit,
      levelNames,
      loaderLibraryShelves,
      fixtureDividerRows,
      baseStoreCode,
    }),
    [basePog, products, unit, levelNames, loaderLibraryShelves, fixtureDividerRows, baseStoreCode],
  );

  return (
    <RulesSection title={t("planogramRules.dividers.title")} area={area} isDisabled={isDisabled}>
      <OSummary
        items={summary}
        action={
          <LinkButton
            type="button"
            aria-expanded={isOpen}
            onClick={() => {
              setIsOpen(!isOpen);
              setIsDetailsOpen(false);
            }}
            disabled={isDisabled}
          >
            {t("planogramRules.tellO.link")}
          </LinkButton>
        }
      >
        {isOpen && (
          <TellO
            policy={policy}
            setPolicy={setPolicy}
            data={tellOData}
            previous={previous}
            setPrevious={setPrevious}
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
            isLoading={isLoading}
            isDisabled={isDisabled}
            isFreeTextDisabled={isFreeTextDisabled}
            basePogId={basePogId}
            interpret={interpret}
            saveShelf={saveShelf}
          />
        )}
      </OSummary>
      {isOpen && isDetailsOpen && (
        <>
          <Flex column padding="0 10px 10px" gap="4px">
            <Text variant="small1" isDisabled={isDisabled}>
              <span id={headingId}>{t("planogramRules.dividers.heading")}</span>
            </Text>
            <Muted data-note="panel">
              {t(
                isShelfMode
                  ? "planogramRules.dividers.explanationShelfMode"
                  : "planogramRules.dividers.explanation",
              )}
            </Muted>
            {badData.isWrong && (
              <InlineMessage status="warning">{t("planogramRules.dividers.badData")}</InlineMessage>
            )}
          </Flex>

          <Columns>
            <Panel role="group" aria-labelledby={headingId}>
              <Flex gap="8px 16px" align="start" flexWrap="wrap">
                <Size
                  field="divider-width"
                  label={t("planogramRules.dividers.dividerWidth")}
                  value={toDisplayLength(hardware?.width ?? 0)}
                  setValue={(value) => setHardware({ width: fromDisplayLength(value) })}
                  unit={unit}
                  note={isWidthFromBase ? t("planogramRules.dividers.fromBase") : null}
                  error={widthProblem}
                  isFocused={widthProblem !== null}
                  isDisabled={isDisabled}
                />
                <Size
                  field="loader-depth"
                  label={t("planogramRules.dividers.loaderDepth")}
                  value={toDisplayLength(loaderDepthInUse(policy, loaders.evidence) ?? 0)}
                  setValue={setLoaderDepth}
                  unit={unit}
                  note={isShelfMode ? null : noteSource && t(NOTE_KEYS[noteSource])}
                  error={depthProblem}
                  isFocused={widthProblem === null && depthProblem !== null}
                  // "Stay with the shelf" uses each shelf's own hardware depth.
                  isDisabled={isDisabled || isShelfMode}
                />
                <LoaderModeChoice
                  mode={loaders.mode}
                  setMode={(mode) => setPolicy(withLoaderMode(policy, mode))}
                  isDisabled={isDisabled}
                />
              </Flex>
              <Flex justify="between" align="center" gap="10px" flexWrap="wrap">
                <Muted role="status" aria-live="polite">
                  {t("planogramRules.dividers.tickCounts", {
                    dividers: dividerTicks.size,
                    loaders: loaders.ticked.size,
                    total: products.length,
                  })}
                </Muted>
                <Flex gap="12px" align="center">
                  <LinkButton
                    type="button"
                    onClick={() => setPolicy(resetToBase(policy))}
                    disabled={isDisabled || badData.isWrong}
                    title={badData.isWrong ? t("planogramRules.dividers.badDataTitle") : undefined}
                  >
                    {t("planogramRules.dividers.resetToBase")}
                  </LinkButton>
                  <LinkButton
                    type="button"
                    onClick={() => setPolicy(clearAllTicks(policy))}
                    disabled={isDisabled || nothingTicked}
                  >
                    {t("planogramRules.dividers.clearAll")}
                  </LinkButton>
                </Flex>
              </Flex>
              <Flex gap="10px" align="center" margin="6px 0 0">
                <Flex width="140px" flexShrink={0}>
                  <Select<DividerCdtLevel>
                    title={t("planogramRules.dividers.groupBy")}
                    options={DIVIDER_CDT_LEVELS.map((level) => ({
                      label: dividerGroupLevelLabel(level, levelNames),
                      value: level,
                    }))}
                    value={groupLevel}
                    setValue={setGroupLevel}
                    isDisabled={isDisabled}
                  />
                </Flex>
                {products.length > SEARCH_FROM && (
                  <Search
                    search={search}
                    setSearch={setSearch}
                    placeholder={t("planogramRules.dividers.searchProducts")}
                  />
                )}
              </Flex>
              <ListRow aria-hidden="true" data-column-headers>
                <Cell width={EXPANDER_WIDTH} />
                {COLUMNS.map(({ column, header }) => (
                  <ColumnHeader key={column} width={TICK_WIDTHS[column]}>
                    {t(header)}
                  </ColumnHeader>
                ))}
                {isShelfMode && (
                  <ColumnNote data-column-note="loaders">
                    {t("planogramRules.dividers.shelfHardwareDecides")}
                  </ColumnNote>
                )}
              </ListRow>
              <Scroll>
                <List aria-label={t("planogramRules.dividers.products")}>
                  {groups.map((group) => {
                    const codes = group.products.map((product) => product.productCode);
                    const counts = COLUMNS.map(
                      ({ column }) => codes.filter((code) => ticked[column].has(code)).length,
                    );
                    const name = group.value || t("planogramRules.dividers.noGroup");
                    const isGroupOpen = query !== "" || expanded.has(group.value);
                    return (
                      <li key={group.value}>
                        <ListRow>
                          <Cell width={EXPANDER_WIDTH}>
                            <ExpandButton
                              type="button"
                              aria-expanded={isGroupOpen}
                              aria-label={t(
                                isGroupOpen
                                  ? "planogramRules.dividers.hideGroup"
                                  : "planogramRules.dividers.showGroup",
                                { name },
                              )}
                              onClick={() => toggleExpanded(group.value)}
                            >
                              {isGroupOpen ? "▾" : "▸"}
                            </ExpandButton>
                          </Cell>
                          {COLUMNS.map(({ column, header }, index) => {
                            const count = counts[index] ?? 0;
                            return (
                              <Cell key={column} width={TICK_WIDTHS[column]}>
                                <TriStateBox
                                  state={
                                    count === 0
                                      ? false
                                      : count === codes.length
                                        ? true
                                        : ("mixed" as const)
                                  }
                                  label={`${t(header)}: ${name}`}
                                  isDisabled={isDisabled}
                                  onChange={(isTicked) => setTicked(column, codes, isTicked)}
                                />
                              </Cell>
                            );
                          })}
                          <RowName>
                            <strong>{name}</strong>
                            <Muted>
                              {t("planogramRules.dividers.groupCounts", {
                                counts: counts.join(" · "),
                                total: codes.length,
                              })}
                            </Muted>
                          </RowName>
                        </ListRow>
                        {isGroupOpen && (
                          <List style={{ padding: "4px 0" }} aria-label={name}>
                            {group.products.map((product) => {
                              const productName = product.name || product.productCode;
                              return (
                                <li key={product.productCode} data-product={product.productCode}>
                                  <ListRow>
                                    <Cell width={EXPANDER_WIDTH} />
                                    {COLUMNS.map(({ column, header }) => (
                                      <Cell key={column} width={TICK_WIDTHS[column]}>
                                        <input
                                          type="checkbox"
                                          aria-label={`${t(header)}: ${productName}`}
                                          checked={ticked[column].has(product.productCode)}
                                          disabled={isDisabled}
                                          onChange={(event) =>
                                            setTicked(
                                              column,
                                              [product.productCode],
                                              event.target.checked,
                                            )
                                          }
                                        />
                                      </Cell>
                                    ))}
                                    <RowName>
                                      <span>{productName}</span>
                                      {product.name && <Muted>{product.productCode}</Muted>}
                                    </RowName>
                                  </ListRow>
                                </li>
                              );
                            })}
                          </List>
                        )}
                      </li>
                    );
                  })}
                  {groups.length === 0 && (
                    <li>
                      <Muted>
                        {isLoading
                          ? t("planogramRules.dividers.loadingProducts")
                          : t("planogramRules.dividers.noProducts")}
                      </Muted>
                    </li>
                  )}
                </List>
              </Scroll>
            </Panel>

            <Panel>
              <Text variant="small1" isDisabled={isDisabled}>
                {t("planogramRules.dividers.onBase")}
              </Text>
              {shelves.length > 0 && (
                <Text variant="caption1" isDisabled={isDisabled}>
                  <strong data-result-total>
                    {describeBaseShelfTotal(shelves, loaders.summary.loaderShelves)}
                  </strong>
                </Text>
              )}
              <Scroll>
                <List aria-label={t("planogramRules.dividers.onBase")}>
                  {shelves.map((shelf) => {
                    const decision = loaderDecisions.get(`${shelf.bayNo}:${shelf.shelfNo}`);
                    const row = withLoaders
                      ? describeBaseShelfResultWithLoader(
                          shelf,
                          describeRowLoaderWithCount(decision, unit, loaders.mode),
                        )
                      : describeBaseShelfResult(
                          shelf,
                          describeRowLoader(decision, unit, loaders.mode),
                        );
                    return (
                      <li key={`${shelf.bayNo}-${shelf.shelfNo}`}>
                        <Text variant="caption1">
                          {`${row.name} · `}
                          <strong style={{ color: shelf.hasDividers ? Color.primary : undefined }}>
                            {row.verdict}
                          </strong>
                          <RowDetail>
                            {withLoaders
                              ? ` ${row.detail} · ${row.loader}`
                              : ` · ${row.loader} · ${row.detail}`}
                          </RowDetail>
                        </Text>
                      </li>
                    );
                  })}
                  {shelves.length === 0 && (
                    <li>
                      <Muted>
                        {isLoading
                          ? t("planogramRules.dividers.readingBase")
                          : t("planogramRules.dividers.noShelves")}
                      </Muted>
                    </li>
                  )}
                </List>
              </Scroll>
            </Panel>
          </Columns>

          <Flex padding="0 10px 10px">
            <CheckRow isDisabled={isDisabled}>
              <input
                type="checkbox"
                checked={keep}
                disabled={isDisabled}
                onChange={(event) =>
                  setPolicy({ ...policy, keepFixtureDividers: event.target.checked })
                }
              />
              {t("planogramRules.dividers.keepFixture")}
            </CheckRow>
          </Flex>

          <Flex padding="0 10px 10px">
            <Disclosure>
              <summary>{t("planogramRules.dividers.ends")}</summary>
              <Flex gap="20px" flexWrap="wrap" margin="8px 0 0">
                <CheckRow isDisabled={isDisabled}>
                  <input
                    type="checkbox"
                    checked={hardware?.atStart ?? true}
                    disabled={isDisabled}
                    onChange={(event) => setHardware({ atStart: event.target.checked })}
                  />
                  {t("planogramRules.dividers.atStart")}
                </CheckRow>
                <CheckRow isDisabled={isDisabled}>
                  <input
                    type="checkbox"
                    checked={hardware?.atEnd ?? true}
                    disabled={isDisabled}
                    onChange={(event) => setHardware({ atEnd: event.target.checked })}
                  />
                  {t("planogramRules.dividers.atEnd")}
                </CheckRow>
              </Flex>
            </Disclosure>
          </Flex>
        </>
      )}
    </RulesSection>
  );
};
